import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Lock, Sparkles, ArrowUpCircle, ArrowRightLeft, UserCog, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { usePreferences } from '../../context/PreferencesContext';
import AuthModal from '../auth/AuthModal';
import UpgradeOptionsModal from './UpgradeOptionsModal';
import { toast } from 'sonner';

/**
 * A component that gates content behind a subscription requirement.
 * 
 * Renders one of four states (priority order):
 *   1. `ok`         → children (hasAccess === true)
 *   2. `unset`      → paid user with no grade preference; CTA to Profile
 *   3. `mismatch`   → paid user viewing a grade their plan doesn't cover;
 *                      primary "Switch to <subscribedGrade>", secondary buy
 *   4. `no-module`  → paid user whose plan doesn't include this module;
 *                      shows the pro-rata upgrade path
 *   5. `free`       → not authenticated or not paid; classic upgrade CTA
 * 
 * Usage:
 * <SubscriptionGate 
 *   hasAccess={canAccessInsights}
 *   featureName="Exam Insights"
 *   moduleKey="exam_insights"      // For upgrade modal
 *   loading={loading}
 *   subscribedGrade={subscribedGrade}   // NEW: for mismatch detection
 *   targetGrade={preferences.grade}     // NEW: for mismatch detection
 * >
 *   <InsightsContent />
 * </SubscriptionGate>
 */
const SubscriptionGate = ({ 
  hasAccess,
  featureName = 'This feature',
  moduleKey = null,  // e.g., 'exam_insights', 'notebook', etc.
  loading = false,
  children,
  description = null,
  compact = false, // For inline gating within a page
  subscribedGrade = null,
  targetGrade = null,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isPaid } = useSubscriptionAccess();
  const { updatePreferences } = usePreferences();
  const [showAuth, setShowAuth] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Show loading state
  if (loading) {
    return compact ? null : (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
      </div>
    );
  }

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  const authed = isAuthenticated && isAuthenticated();

  // Detect grade-mismatch / grade-unset states — mirrors UpgradeModal so users
  // get the same messaging whether they hit a modal-gate (Q-bank Show Answer)
  // or a full-page gate (Notebook / Live Classes).
  const isGradeMismatch =
    authed &&
    isPaid &&
    subscribedGrade &&
    subscribedGrade !== 'all' &&
    targetGrade &&
    subscribedGrade !== targetGrade;
  const isGradeUnset = authed && isPaid && !targetGrade && !subscribedGrade;

  // Paid-but-module-not-in-plan is the ORIGINAL state this gate handled.
  // Only applies when we're NOT in a grade-scoped state.
  const isPaidWithoutModule = authed && isPaid && !isGradeMismatch && !isGradeUnset;

  const handleSwitchGrade = async () => {
    if (!subscribedGrade) return;
    setSwitching(true);
    try {
      await updatePreferences({ grade: subscribedGrade });
      toast.success(`Switched to ${subscribedGrade}`);
      // Reload so grade-scoped fetches re-run against the new preference.
      window.location.reload();
    } catch (err) {
      toast.error('Could not switch grade. Please try from Profile → Preferences.');
    } finally {
      setSwitching(false);
    }
  };

  const handleGoToProfile = () => navigate('/profile');
  const handleBuyOtherGrade = () => {
    navigate(targetGrade ? `/plans?grade=${encodeURIComponent(targetGrade)}` : '/plans');
  };

  // Different messaging for paid vs free users
  const getTitle = () => {
    if (!authed) return `${featureName} Locked`;
    if (isPaidWithoutModule) return `Unlock ${featureName}`;
    return `${featureName} Locked`;
  };

  const getDescription = () => {
    if (description) return description;
    if (isPaidWithoutModule) {
      return `Your current plan doesn't include ${featureName}. Upgrade to a plan that includes this feature.`;
    }
    return `Upgrade to a premium plan to unlock ${featureName} and enhance your exam preparation.`;
  };

  const getButtonText = () => {
    if (!authed) return 'Sign Up to Unlock';
    if (isPaidWithoutModule) return `Unlock ${featureName}`;
    return 'Upgrade to Premium';
  };

  const getButtonIcon = () => {
    if (isPaidWithoutModule) return <ArrowUpCircle className="w-5 h-5 mr-2" />;
    return <Sparkles className="w-5 h-5 mr-2" />;
  };

  const handleUpgrade = () => {
    if (!authed) {
      setShowAuth(true);
      return;
    }
    if (isPaidWithoutModule && moduleKey) {
      setShowUpgradeModal(true);
    } else {
      navigate('/plans');
    }
  };

  // ---------- Compact (inline) variant ----------
  if (compact) {
    // Grade-mismatch inline banner
    if (isGradeMismatch) {
      return (
        <div className="p-4 rounded-lg border bg-blue-50 border-blue-200" data-testid="subscription-gate-mismatch-compact">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-100">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">This plan doesn&apos;t cover {targetGrade}</p>
              <p className="text-sm text-gray-600 truncate">
                Your subscription is for {subscribedGrade}. Switch back or buy a plan for {targetGrade}.
              </p>
            </div>
            <Button
              onClick={handleSwitchGrade}
              disabled={switching}
              size="sm"
              className="flex-shrink-0 bg-[#0A7EA4] hover:bg-[#08678A]"
              data-testid="subscription-gate-switch-grade-btn"
            >
              {switching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
              Switch to {subscribedGrade}
            </Button>
          </div>
        </div>
      );
    }
    // Grade-unset inline banner
    if (isGradeUnset) {
      return (
        <div className="p-4 rounded-lg border bg-amber-50 border-amber-200" data-testid="subscription-gate-unset-compact">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-100">
              <UserCog className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">Set your exam grade first</p>
              <p className="text-sm text-gray-600 truncate">Pick a grade in Profile → Preferences to unlock this feature.</p>
            </div>
            <Button onClick={handleGoToProfile} size="sm" className="flex-shrink-0 bg-[#0A7EA4] hover:bg-[#08678A]" data-testid="subscription-gate-open-profile-btn">
              <UserCog className="w-4 h-4 mr-1" />
              Open Profile
            </Button>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className={`p-4 rounded-lg border ${isPaidWithoutModule ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${isPaidWithoutModule ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Lock className={`w-5 h-5 ${isPaidWithoutModule ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">
                {isPaidWithoutModule ? `Unlock ${featureName}` : `${featureName} - Premium Feature`}
              </p>
              <p className="text-sm text-gray-600 truncate">{getDescription()}</p>
            </div>
            <Button 
              onClick={handleUpgrade}
              size="sm"
              className={`flex-shrink-0 ${isPaidWithoutModule ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'}`}
            >
              {isPaidWithoutModule ? <ArrowUpCircle className="w-4 h-4 mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {isPaidWithoutModule ? 'Unlock' : 'Upgrade'}
            </Button>
          </div>
        </div>
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
        <UpgradeOptionsModal 
          open={showUpgradeModal} 
          onClose={() => setShowUpgradeModal(false)}
          targetModule={moduleKey}
          featureName={featureName}
        />
      </>
    );
  }

  // ---------- Full-page grade-mismatch card ----------
  if (isGradeMismatch) {
    return (
      <Card className="border-2 border-dashed border-blue-300" data-testid="subscription-gate-mismatch-card">
        <CardContent className="py-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="mx-auto mb-6 h-20 w-20 rounded-full flex items-center justify-center bg-blue-100">
              <ArrowRightLeft className="h-10 w-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900">This plan doesn&apos;t cover this grade</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Your active subscription is for <span className="font-semibold text-gray-900">{subscribedGrade}</span>, but you&apos;re currently viewing <span className="font-semibold text-gray-900">{targetGrade}</span> content. Switch back to the grade your plan covers, or purchase a plan for {targetGrade} as well.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={handleSwitchGrade}
                disabled={switching}
                className="bg-[#0A7EA4] hover:bg-[#08678A]"
                data-testid="subscription-gate-switch-grade-btn"
              >
                {switching ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ArrowRightLeft className="w-5 h-5 mr-2" />}
                Switch to {subscribedGrade}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleBuyOtherGrade}
                disabled={switching}
                data-testid="subscription-gate-buy-other-grade-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Buy plan for {targetGrade}
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Switching keeps your existing plan. You can switch back anytime from Profile → Preferences.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------- Full-page grade-unset card ----------
  if (isGradeUnset) {
    return (
      <Card className="border-2 border-dashed border-amber-300" data-testid="subscription-gate-unset-card">
        <CardContent className="py-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="mx-auto mb-6 h-20 w-20 rounded-full flex items-center justify-center bg-amber-100">
              <UserCog className="h-10 w-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900">Set your exam grade first</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Please pick a grade in Profile → Preferences to unlock {featureName}.
            </p>
            <Button
              size="lg"
              onClick={handleGoToProfile}
              className="bg-[#0A7EA4] hover:bg-[#08678A]"
              data-testid="subscription-gate-open-profile-btn"
            >
              <UserCog className="w-5 h-5 mr-2" />
              Open Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------- Full-page default (free / paid-without-module) ----------
  return (
    <>
      <Card className={`border-2 border-dashed ${isPaidWithoutModule ? 'border-amber-300' : 'border-gray-300'}`}>
        <CardContent className="py-12 text-center">
          <div className="max-w-md mx-auto">
            <div className={`mx-auto mb-6 h-20 w-20 rounded-full flex items-center justify-center ${isPaidWithoutModule ? 'bg-gradient-to-br from-amber-100 to-orange-100' : 'bg-gradient-to-br from-blue-100 to-purple-100'}`}>
              <Lock className={`h-10 w-10 ${isPaidWithoutModule ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900">{getTitle()}</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {getDescription()}
            </p>
            <Button 
              size="lg"
              onClick={handleUpgrade}
              className={`${isPaidWithoutModule ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'}`}
              data-testid="subscription-gate-upgrade-btn"
            >
              {getButtonIcon()}
              {getButtonText()}
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              {isPaidWithoutModule 
                ? 'Pro-rata pricing - only pay the difference'
                : 'Get unlimited access to all premium features'
              }
            </p>
          </div>
        </CardContent>
      </Card>
      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      <UpgradeOptionsModal 
        open={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        targetModule={moduleKey}
        featureName={featureName}
      />
    </>
  );
};

export default SubscriptionGate;
